import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/auth.service';
import {
  BarcodeResolution,
  CountShelfStockDto,
  CreateWarehouseCellDto,
  PickStockByCellDto,
  PlaceStockByCellDto,
  ProductCellBalance,
  ProductInWarehouse,
  ResolveBarcodeDto,
  ResolveVirtualCellOffsetDto,
  ResolveVirtualCellOffsetResult,
  SearchStockCellBalanceDto,
  SearchStockCellMovementDto,
  SearchWarehouseCellDto,
  SetCellStockDto,
  StockCellMovementSearchResponse,
  VirtualCellOffsetPreview,
  UpdateProductCellInWarehouse,
  UpdateWarehouseCellDto,
  Warehouse,
  WarehouseCell
} from 'src/typings';

@Injectable({
  providedIn: 'root'
})
export class WarehouseService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  getAllList() {
    return this.http.get<Warehouse[]>(`${environment.BACKEND}/warehouse/list`);
  }

  getManagementList() {
    return this.http.get<Warehouse[]>(`${environment.BACKEND}/warehouse/management-list`);
  }

  getOne(id: number) {
    return this.http.get<Warehouse>(`${environment.BACKEND}/warehouse/${id}`);
  }

  updateCell(payload: UpdateProductCellInWarehouse) {
    const resp = this.http.put<ProductInWarehouse>(
      `${environment.BACKEND}/warehouse/update_cell`,
      payload
    );
    return resp;
  }

  create(description: string, cityId?: number) {
    return this.http.post<Warehouse>(`${environment.BACKEND}/warehouse/create`, {
      description,
      city_id: cityId
    });
  }

  update(id: number, description: string, cityId?: number) {
    return this.http.put<Warehouse>(`${environment.BACKEND}/warehouse/update`, {
      id,
      description,
      city_id: cityId
    });
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/warehouse/${id}`);
  }

  searchCells(payload: SearchWarehouseCellDto) {
    return this.http.post<WarehouseCell[]>(`${environment.BACKEND}/stock/cell/search`, payload);
  }

  createCell(payload: CreateWarehouseCellDto) {
    return this.http.post<WarehouseCell>(`${environment.BACKEND}/stock/cell/create`, payload);
  }

  updateWarehouseCell(payload: UpdateWarehouseCellDto) {
    return this.http.put<WarehouseCell>(`${environment.BACKEND}/stock/cell/update`, payload);
  }

  getCellBalances(payload: SearchStockCellBalanceDto) {
    return this.http.post<ProductCellBalance[]>(
      `${environment.BACKEND}/stock/cell/balance/search`,
      payload
    );
  }

  searchCellMovements(payload: SearchStockCellMovementDto) {
    return this.http
      .post<StockCellMovementSearchResponse>(
        `${environment.BACKEND}/stock/cell/movement/search`,
        payload
      )
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((row) => ({
            ...row,
            qty: Number(row.qty || 0),
            createdAt: new Date(row.createdAt)
          }))
        }))
      );
  }

  placeStock(payload: PlaceStockByCellDto) {
    return this.http.put<ProductCellBalance[]>(`${environment.BACKEND}/stock/cell/place`, payload);
  }

  pickStock(payload: PickStockByCellDto) {
    return this.http.put<ProductCellBalance[]>(`${environment.BACKEND}/stock/cell/pick`, payload);
  }

  setCellStock(payload: SetCellStockDto) {
    return this.http.put<ProductCellBalance[]>(`${environment.BACKEND}/stock/cell/set`, payload);
  }

  previewVirtualCellOffset(payload: ResolveVirtualCellOffsetDto) {
    return this.http.post<VirtualCellOffsetPreview>(
      `${environment.BACKEND}/stock/cell/virtual-offset/preview`,
      payload
    );
  }

  resolveVirtualCellOffset(payload: ResolveVirtualCellOffsetDto) {
    return this.http.put<ResolveVirtualCellOffsetResult>(
      `${environment.BACKEND}/stock/cell/virtual-offset/resolve`,
      payload
    );
  }

  countShelfStock(payload: CountShelfStockDto) {
    return this.http.put<ProductCellBalance[]>(
      `${environment.BACKEND}/stock/cell/count/shelf`,
      payload
    );
  }

  resolveBarcode(payload: ResolveBarcodeDto): Observable<BarcodeResolution> {
    return this.http.post<BarcodeResolution>(
      `${environment.BACKEND}/stock/barcode/resolve`,
      payload
    );
  }
}
